import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alignment,
  Button,
  IconName,
  Menu,
  MenuItem,
  Navbar,
  Popover,
} from '@blueprintjs/core';

import { useAppSelector } from '../../../../hooks';
import { AuthState } from '../../types';

import LoginHeaderButton from '../LoginHeaderButton';

import {
  Container,
  DesktopMenuButtons,
  InnerContainer,
  MobileMenuIcon,
} from './styles';

type MainContainerProps = {
  children: React.ReactNode;
};

interface HeaderAction {
  name: string;
  route: string;
  icon: IconName;
  role?: string;
}

const action: HeaderAction[] = [
  {
    name: 'Enviar',
    route: '/send',
    icon: 'plus',
  },
  {
    name: 'Wavelights',
    route: '/wavelights',
    icon: 'book',
    role: 'admin',
  },
  {
    name: 'Users',
    route: '/users',
    icon: 'user',
    role: 'admin',
  },
];

const MainContainer = ({ children }: MainContainerProps) => {
  const state: AuthState = useAppSelector((state) => state.auth);
  const navigate = useNavigate();

  const goTo = useCallback(
    (route: string) => {
      navigate(route);
    },
    [navigate],
  );

  const filterButtons = useCallback(
    (menuItem: HeaderAction) => {
      if (!menuItem.role) {
        return true;
      } else {
        const role = menuItem.role;
        const userRoles = state.profile?.roles;

        if (!userRoles) {
          return false;
        }

        return userRoles?.indexOf(role) >= 0;
      }
    },
    [state.profile?.roles],
  );

  const buildMobileButtons = useCallback(() => {
    return (
      <Menu>
        {action
          .filter((item) => filterButtons(item))
          .map((item) => (
            <MenuItem
              key={item.name}
              className="bp5-minimal"
              icon={item.icon}
              text={item.name}
              onClick={() => goTo(item.route)}
            />
          ))}
      </Menu>
    );
  }, [filterButtons, goTo]);

  return (
    <Container>
      <Navbar>
        <Navbar.Group align={Alignment.LEFT}>
          <MobileMenuIcon>
            <Popover content={buildMobileButtons()} placement="bottom">
              <Button className="bp5-minimal" icon="menu" />
            </Popover>
          </MobileMenuIcon>
          <Navbar.Heading>Wavelight Toolkit</Navbar.Heading>
          <Navbar.Divider />
          <DesktopMenuButtons>
            {action
              .filter((item) => filterButtons(item))
              .map((item) => (
                <Button
                  key={item.name}
                  icon={item.icon}
                  text={item.name}
                  onClick={() => navigate(item.route)}
                  className="bp5-minimal"
                />
              ))}
          </DesktopMenuButtons>
        </Navbar.Group>
        <Navbar.Group align={Alignment.RIGHT}>
          <LoginHeaderButton />
        </Navbar.Group>
      </Navbar>
      <InnerContainer>{children}</InnerContainer>
    </Container>
  );
};

export default MainContainer;
